/**
 * Help panel (W2-14 onboarding-wizard; DESIGN.md §5.14 "Help panel (F1)"; `DIALOG.help`, a non-modal side sheet):
 * searchable short articles from every module's help topics, each with "Show me" when it names a part of the
 * editor (the part is brought on screen and ringed by a coach mark, while the panel stays open). Full-level topics
 * carry a "Full" badge in Guided level (they are never hidden). The "For RPE users" article adds the keyboard preset
 * switch. The footer offers the tour, the shortcut sheet and the welcome question. Shortcuts in articles are the
 * keys the active keymap binds now (markdownLite.tsx `[[command]]`; re-rendered when the keymap changes, so the
 * preset switch updates the article at once), also in search.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import type { Chord, DialogProps, HelpTopicDef } from '../../app/registry/types';
import type { KeymapPresetId } from '../../app/registry/types';
import { Badge, Button, EmptyState, Icon, Segmented, Sheet, TextField } from '../../ui';
import { CoachMark } from './CoachMark';
import { m } from './help.messages';
import { HELP_ORDER, searchTopics, showMeOf, TOPIC, type SearchableTopic } from './helpTopics';
import { prefersReducedMotion, useServicesText, useSettingsValue } from './hooks';
import { bindingsOf, boundChord, displayChord, keyLabelParam, useKeyBindings, type KeyRef } from './keys';
import { MarkdownLite, plainText } from './markdownLite';
import { helpOpen, registerHelpElement, takeShowMe } from './onboardingState';
import s from './Onboarding.module.css';
import { findHelpTarget, revealHelpTarget } from './showMe';

/** How long a "Show me" ring stays (ms). */
export const SPOTLIGHT_MS = 8000;

/**
 * Every registered topic: those named in `order` first, in that order (helpTopics.ts `HELP_ORDER`: this package's
 * and the Publish module's), then the others by module order.
 */
export function orderedTopics(all: readonly HelpTopicDef[], order: readonly string[]): HelpTopicDef[] {
  const rank = new Map(order.map((id, i) => [id, i]));
  const listed = all.filter((t) => rank.has(t.id)).sort((a, b) => (rank.get(a.id) ?? 0) - (rank.get(b.id) ?? 0));
  const others = all.filter((t) => !rank.has(t.id));
  return [...listed, ...others];
}

export function HelpPanel(p: DialogProps<'help'>) {
  const { services } = p;
  const text = useServicesText(services);
  const settings = useSettingsValue(services);
  const reduced = prefersReducedMotion(settings);
  const guided = settings.general.level === 'guided';
  const [query, setQuery] = useState('');
  const [topicId, setTopicId] = useState<string | null>(p.props.topic ?? null);
  const [spotlight, setSpotlight] = useState<{
    readonly el: Element;
    readonly title: string;
    readonly body: string | null;
    readonly avoidEl: Element | null;
  } | null>(null);
  const [missing, setMissing] = useState(false);
  const closed = useRef(false);
  const { keymap } = services;
  // A new list on every keymap change, so the memoised search below follows rebinding.
  const bindings = useKeyBindings(keymap);
  const keys = useCallback(
    (ref: KeyRef): Chord | null => {
      const chord = boundChord(bindingsOf(bindings), ref);
      return chord === null ? null : displayChord(chord);
    },
    [bindings],
  );
  const keyLabel = useCallback(
    (chord: Chord): string => {
      try {
        return keymap.label(chord);
      } catch {
        return chord;
      }
    },
    [keymap],
  );
  // The sheet's content mounts in a portal after this component: register it when it is attached.
  const root = useCallback((el: HTMLDivElement | null) => (el === null ? undefined : registerHelpElement(el)), []);

  useEffect(() => {
    helpOpen.set(true);
    return () => {
      helpOpen.set(false);
    };
  }, []);

  const topics: SearchableTopic[] = useMemo(
    () =>
      orderedTopics(services.registry.helpTopics(), HELP_ORDER).map((topic) => ({
        topic,
        title: text.t(topic.title),
        body: plainText(text.t(topic.body), keys, keyLabel),
      })),
    [services, text, keys, keyLabel],
  );
  const results = useMemo(() => searchTopics(topics, query), [topics, query]);
  const current = topicId === null ? undefined : topics.find((x) => x.topic.id === topicId);

  const showMe = async (topic: HelpTopicDef): Promise<void> => {
    const target = topic.showMe;
    if (target === undefined) return;
    setMissing(false);
    const el = await revealHelpTarget(services, target);
    if (closed.current) return;
    if (el === null) {
      setMissing(true);
      return;
    }
    // UX-094 (CR-W3-08-6): the spotlight carries the topic's short body and keeps clear of the area the text
    // talks about (`showMeOf`: body + avoid, the side map until HelpTopicDef.showMe.avoid is contracted).
    const sm = showMeOf(topic.id);
    const body = sm === null ? null : text.t(sm.body);
    const avoidEl = sm?.avoid === undefined ? null : findHelpTarget(services, sm.avoid);
    setSpotlight({ el, title: text.t(topic.title), body, avoidEl });
  };

  // "Show me" requested from the palette before the panel opened.
  const requested = useRef(false);
  useEffect(() => {
    if (requested.current || current === undefined) return;
    requested.current = true;
    if (takeShowMe(current.topic.id)) {
      const topic = current.topic;
      queueMicrotask(() => {
        void showMe(topic);
      });
    }
  });

  // The ring goes away by itself.
  useEffect(() => {
    if (spotlight === null) return undefined;
    const timer = window.setTimeout(() => {
      setSpotlight(null);
    }, SPOTLIGHT_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [spotlight]);

  const close = (): void => {
    closed.current = true;
    p.close(null);
  };

  // F1 also closes the panel from its search field: the keyboard router leaves F-keys to a focused text field.
  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (e.key !== 'F1' || e.defaultPrevented || e.ctrlKey || e.altKey || e.metaKey || e.shiftKey) return;
    e.preventDefault();
    close();
  };

  const open = (id: string): void => {
    setTopicId(id);
    setMissing(false);
  };

  const preset = settings.keyboard.preset;
  const setPreset = (v: KeymapPresetId): void => {
    if (services.settings.value.keyboard.preset !== v) services.settings.set('keyboard', 'preset', v);
    if (services.keymap.preset !== v) services.keymap.setPreset(v);
  };

  return (
    <>
      <Sheet
        open
        title={text.t(m.panelTitle)}
        onClose={close}
        footer={
          <div className={s.helpFooter}>
            <Button size="sm" icon="Footprints" command="tour.start">
              {text.t(m.takeTour)}
            </Button>
            <Button size="sm" icon="Keyboard" command="help.cheatSheet">
              {text.t(m.shortcuts)}
            </Button>
            <Button size="sm" icon="Hand" command="help.welcome">
              {text.t(m.welcome)}
            </Button>
          </div>
        }
      >
        <div ref={root} className={s.helpPanel} onKeyDown={onKeyDown}>
          {current === undefined ? (
            <>
              <TextField
                label={text.t(m.search)}
                type="search"
                value={query}
                placeholder={{ key: m.searchPlaceholder }}
                onChange={setQuery}
                onCommit={setQuery}
              />
              {results.length === 0 ? (
                <EmptyState
                  icon="SearchX"
                  title={{ key: m.noResultsTitle }}
                  body={{
                    key: m.noResultsBody,
                    params: { palette: keyLabelParam(keymap, { command: 'app.palette' }) },
                  }}
                />
              ) : (
                <ul className={s.topicList} aria-label={text.t(m.topicsLabel)}>
                  {results.map((r) => (
                    <li key={r.topic.id}>
                      <button
                        type="button"
                        className={s.topicButton}
                        onClick={() => {
                          open(r.topic.id);
                        }}
                      >
                        <span className={s.topicTitle}>
                          {r.title}
                          {guided && r.topic.level === 'full' && (
                            <Badge kind="full" label={{ key: m.fullBadge }} tooltip={{ key: m.fullBadgeHint }} />
                          )}
                        </span>
                        <span className={s.topicExcerpt}>{r.body.slice(0, 140)}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <article className={s.helpArticle} aria-label={text.t(m.articleOf, { title: current.title })}>
              <Button
                variant="ghost"
                size="sm"
                icon="ArrowLeft"
                onClick={() => {
                  setTopicId(null);
                  setMissing(false);
                }}
              >
                {text.t(m.allTopics)}
              </Button>
              <h3 className={s.articleTitle}>
                {current.title}
                {guided && current.topic.level === 'full' && (
                  <Badge kind="full" label={{ key: m.fullBadge }} tooltip={{ key: m.fullBadgeHint }} />
                )}
              </h3>
              <MarkdownLite source={text.t(current.topic.body)} keys={keys} />
              {current.topic.showMe !== undefined && (
                <Button
                  icon="Eye"
                  onClick={() => {
                    void showMe(current.topic);
                  }}
                >
                  {text.t(m.showMe)}
                </Button>
              )}
              {missing && (
                <p className={s.coachHint} role="status">
                  <Icon name="Info" size={14} />
                  <span>{text.t(m.showMeMissing)}</span>
                </p>
              )}
              {current.topic.id === TOPIC.rpeUsers && (
                <section className={s.keymapBox} aria-label={text.t(m.keymapTitle)}>
                  <Segmented<KeymapPresetId>
                    label={text.t(m.keymapTitle)}
                    value={preset}
                    options={[
                      { value: 'default', label: text.t(m.keymapDefault) },
                      { value: 'rpe', label: text.t(m.keymapRpe) },
                    ]}
                    onChange={setPreset}
                  />
                  <p className={s.muted}>{text.t(m.keymapNow)}</p>
                  {services.registry.command('settings.open') !== undefined && (
                    <Button
                      size="sm"
                      icon="Settings"
                      onClick={() => {
                        void services.registry.run('settings.open', { section: 'keyboard' }, 'button');
                      }}
                    >
                      {text.t(m.openKeyboardSettings)}
                    </Button>
                  )}
                </section>
              )}
            </article>
          )}
        </div>
      </Sheet>
      {spotlight !== null && (
        <CoachMark
          kind="spotlight"
          target={spotlight.el}
          avoid={spotlight.avoidEl}
          title={spotlight.title}
          closeLabel={{ key: m.spotlightClose }}
          onClose={() => {
            setSpotlight(null);
          }}
          reducedMotion={reduced}
          footer={
            <Button
              size="sm"
              variant="primary"
              onClick={() => {
                setSpotlight(null);
              }}
            >
              {text.t(m.spotlightDismiss)}
            </Button>
          }
        >
          {spotlight.body === null ? undefined : <p>{spotlight.body}</p>}
        </CoachMark>
      )}
    </>
  );
}
